import classNames from 'classnames';
import Link from 'next/link';
import { AnchorHTMLAttributes, ButtonHTMLAttributes, FC, Fragment } from 'react';

import { Membership, SubscriptionPackage } from '@bpm-web-app/supreme-api-sdk';
import { Button } from '../../components/button/button';
import styles from './card.module.css';
import { useButtonLabel } from './hooks/useButtonLabel';

type Props = {
    title: string
    subTitle?: string
    strikeThroughText?: string
    amount: number | string
    customAmountText?: React.ReactNode
    link: string
    library?: string | string[]
    customLibrary?: React.ReactNode
    description?: string
    period?: string
    hidePeriodSlash?: boolean
    tag?: string
    theme: string
    disabled?: boolean
    features?: (string | { label: string, included?: boolean, is_extra?: boolean, color?: string })[]
    listTitle?: string
    subscriptionPackage: SubscriptionPackage
    currentMembership?: Membership
};

export const Card: FC<Props> = ({
    subscriptionPackage,
    currentMembership,
    tag,
    library,
    customLibrary,
    theme,
    title,
    subTitle,
    strikeThroughText,
    amount,
    customAmountText,
    period = 'mo',
    hidePeriodSlash = false,
    link,
    disabled,
    listTitle,
    features,
}) => {
    const buttonLabel = useButtonLabel(subscriptionPackage, currentMembership);
    const isDisabled = disabled || buttonLabel === 'Unavailable';

    const styleProps = {
        className: classNames(styles['card__container'], styles[`card__container--${theme || title.toLowerCase()}`], {
            [styles['card__container--highlighted']]: !!tag,
        }),
        style: {
            '--primary-color': `var(--color-subscription-${(theme)})`
        } as React.CSSProperties
    };

    const wrapper = link && !isDisabled ? {
        component: Link as unknown as FC<AnchorHTMLAttributes<HTMLAnchorElement>>,
        props: {
            href: link,
            wrapper: {
                component: 'a',
                props: {
                    ...styleProps,
                }
            }
        }
    } : {
        component: 'button' as unknown as FC<ButtonHTMLAttributes<HTMLButtonElement>>,
        props: {
            ...styleProps,
            disabled: isDisabled,
            wrapper: {
                component: Fragment,
                props: null
            }
        }
    };

    return (
        <wrapper.component
            {...wrapper.props}
        >
            <wrapper.props.wrapper.component {...wrapper.props.wrapper.props}>
                {!!tag && <span className={classNames(styles['card__tag'])}>{tag}</span>}
                <h3 className={styles['card__title']}>{title}</h3>
                {strikeThroughText && <p className={styles['card__title__strike-through']}>{strikeThroughText}</p>}
                {customAmountText && <p className={styles['card__price']}>{customAmountText}</p>}
                {!customAmountText && <p className={styles['card__price']}>${amount}<span>{hidePeriodSlash ? '' : '/'}{period}</span></p>}
                {subTitle && <p className={styles['card__subtitle']}>{subTitle}</p>}
                {!!subscriptionPackage.description && (
                    <p className={styles['card__description']}>{subscriptionPackage.description}</p>
                )}

                {!!library && (
                    <p className={styles['card__library']}>
                        {customLibrary || (typeof library === 'string'
                            ? `${library} Library`
                            : `${library.join(' & ')} Libraries`)}

                    </p>
                )}

                <Button
                    as="span"
                    variant="primary"
                    className={classNames(styles['card__button'], {
                        [styles['card__button--disabled']]: isDisabled
                    })}
                >
                    {buttonLabel}
                </Button>

                {!!listTitle && <p className={styles['card__list-title']}>{listTitle}</p>}
                {!!features && (
                    <ul className={styles['card__features']}>
                        {features.map((item) => (
                            <li
                                key={typeof item === 'string' ? item : item.label}
                                className={classNames({
                                    [styles['not-included']]: typeof item === 'object' && item.included === false,
                                    [styles['is-extra']]: typeof item === 'object' && item.is_extra
                                })}
                                style={typeof item === 'object' && item.color ? { color: item.color } : undefined}
                            >
                                {typeof item === 'string' ? item : item.label}
                            </li>
                        ))}
                    </ul>
                )}
            </wrapper.props.wrapper.component>
        </wrapper.component>
    );
};
